import { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSetPurchaser } from '@/hooks/queries/usePurchasers';
import type { CampaignUserDto } from '@signalvote/shared';

interface MakesPurchasesControlProps {
  volunteer: CampaignUserDto;
  canManageFinance: boolean;
  isSelf: boolean;
}

function mayToggle(canManageFinance: boolean, isSelf: boolean, isPurchaser: boolean) {
  return canManageFinance || (isSelf && isPurchaser);
}

export function MakesPurchasesControl({ volunteer, canManageFinance, isSelf }: MakesPurchasesControlProps) {
  const { mutate: setPurchaser, isPending, failure } = useSetPurchaser();
  const [confirming, setConfirming] = useState(false);
  const isPurchaser = volunteer.isPurchaser;
  const name = `${volunteer.firstName} ${volunteer.lastName}`;

  const save = (next: boolean) => {
    setPurchaser(
      { userId: volunteer.userId, isPurchaser: next, purchaserVersion: volunteer.purchaserVersion },
      { onSettled: () => setConfirming(false) },
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <Switch
        aria-label={`Makes purchases: ${name}`}
        checked={isPurchaser}
        disabled={isPending || !mayToggle(canManageFinance, isSelf, isPurchaser)}
        onCheckedChange={(next) => (next ? setConfirming(true) : save(false))}
      />
      {failure && (
        <span role="alert" className="text-xs text-destructive">
          Couldn't save. Refresh and try again.
        </span>
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add {name} as someone who makes purchases?</AlertDialogTitle>
            <AlertDialogDescription>
              {name} will be asked about campaign card charges. They will see only the charges they are asked about.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isPending}
              onClick={(event) => {
                event.preventDefault();
                save(true);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
